import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authMode, setAuthMode] = useState('login'); // 'login' ou 'register'
    const [showUserMenu, setShowUserMenu] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, isAuthenticated, isAdmin } = useAuth();

    // Détecter si on est dans l'espace admin/compte
    const isAdminOrAccountRoute =
        location.pathname.startsWith('/admin') ||
        location.pathname.startsWith('/mon-compte');

    // Fonction pour fermer le menu mobile
    const closeMenu = () => {
        setMenuOpen(false);
    };

    // Fonction pour ouvrir le modal d'authentification
    const openAuthModal = (mode) => {
        setAuthMode(mode);
        setShowAuthModal(true);
        setMenuOpen(false);
    };

    // Fonction pour fermer le modal
    const closeAuthModal = () => {
        setShowAuthModal(false);
    };

    // Fonction de déconnexion
    const handleLogout = () => {
        logout();
        setMenuOpen(false);
        setShowUserMenu(false);
    };

    // Fonction pour obtenir l'initiale du prénom
    const getUserInitial = () => {
        if (user?.name) {
            return user.name.charAt(0).toUpperCase();
        } else if (user?.email) {
            return user.email.charAt(0).toUpperCase();
        }
        return 'U';
    };

    // Fermer le menu utilisateur quand on clique ailleurs
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showUserMenu && !event.target.closest('.user-menu-container')) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showUserMenu]);

    // Appliquer un style global différent pour l'espace public vs admin/compte
    useEffect(() => {
        if (isAdminOrAccountRoute) {
            document.body.classList.add('admin-style');
            document.body.classList.remove('public-style');
        } else {
            document.body.classList.add('public-style');
            document.body.classList.remove('admin-style');
        }

        return () => {
            document.body.classList.remove('admin-style');
            document.body.classList.remove('public-style');
        };
    }, [isAdminOrAccountRoute]);

    // Fonction pour gérer le défilement fluide vers les sections
    const handleScrollToSection = (event, targetId) => {
        event.preventDefault();
        const targetElement = document.getElementById(targetId.substring(1));
        if (targetElement) {
            window.scrollTo({
                top: targetElement.offsetTop,
                behavior: 'smooth'
            });
            setMenuOpen(false); // Ferme le menu après avoir cliqué sur un lien sur mobile
        }
    };

    // Effet pour ajouter/supprimer l'écouteur de défilement pour le changement de fond de la barre de navigation
    useEffect(() => {
        const handleScroll = () => {
            const offset = window.scrollY;
            if (offset > 50) { // Change le fond après 50px de défilement
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const isHomePage = location.pathname === '/';

    return (
        <nav className={`navbar ${(scrolled || !isHomePage) ? 'scrolled' : ''}`}>
            <div className="navbar-container">
                {/* Section Logo (Gauche) */}
                <div className="nav-section left-section">
                    <Link to="/" className="navbar-brand">
                        <img src="/images/logo.jpeg" alt="Christophe Dépanne Logo" className="navbar-logo-img" />
                        Christophe Dépanne
                    </Link>
                </div>

                {/* Section Liens (Centre) - Caché sur mobile */}
                <div className="nav-section center-section desktop-only">
                    <ul className="navbar-links desktop-links">
                        <li>
                            <Link to="/" className={location.pathname === '/' ? 'active' : ''}>
                                Accueil
                            </Link>
                        </li>
                        <li>
                            <Link to="/mecanique" className={location.pathname === '/mecanique' ? 'active' : ''}>
                                Mécanique
                            </Link>
                        </li>
                        <li>
                            <Link to="/vente-auto" className={location.pathname === '/vente-auto' ? 'active' : ''}>
                                Vente Auto
                            </Link>
                        </li>
                        <li>
                            <Link to="/blog" className={location.pathname === '/blog' ? 'active' : ''}>
                                Blog
                            </Link>
                        </li>
                        <li>
                            <Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''}>
                                Contact
                            </Link>
                        </li>
                    </ul>
                </div>

                {/* Section Actions (Droite) - Caché sur mobile */}
                <div className="nav-section right-section desktop-only">
                    <ul className="navbar-actions desktop-links">
                        {!isAuthenticated ? (
                            <>
                                <li className="auth-desktop">
                                    <button onClick={() => openAuthModal('login')} className="auth-btn-desktop">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                        </svg>
                                        Connexion
                                    </button>
                                </li>
                                <li className="auth-desktop">
                                    <button
                                        onClick={() => navigate('/inscription')}
                                        className="auth-btn-desktop register"
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                        </svg>
                                        Inscription
                                    </button>
                                </li>
                            </>
                        ) : (
                            <li className="auth-desktop">
                                <div className="user-menu-container">
                                    <button
                                        className="user-avatar-btn"
                                        onClick={() => setShowUserMenu(!showUserMenu)}
                                        aria-label="Menu utilisateur"
                                    >
                                        <div className="avatar-circle">
                                            {getUserInitial()}
                                        </div>
                                        <svg
                                            width="12"
                                            height="12"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            className={`dropdown-arrow ${showUserMenu ? 'rotated' : ''}`}
                                        >
                                            <path d="M7 10l5 5 5-5z" />
                                        </svg>
                                    </button>

                                    {showUserMenu && (
                                        <div className="user-dropdown-menu">
                                            <div className="user-info-header">
                                                <div className="avatar-circle-large">
                                                    {getUserInitial()}
                                                </div>
                                                <div className="user-details">
                                                    <div className="user-name">{user?.name || 'Utilisateur'}</div>
                                                    <div className="user-email">{user?.email}</div>
                                                </div>
                                            </div>

                                            <div className="menu-separator"></div>

                                            <div className="menu-items">
                                                <Link
                                                    to="/mon-compte"
                                                    className="user-menu-item"
                                                    onClick={() => setShowUserMenu(false)}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                                    </svg>
                                                    Mon Compte
                                                </Link>

                                                {isAdmin && (
                                                    <Link
                                                        to="/admin"
                                                        className="user-menu-item"
                                                        onClick={() => setShowUserMenu(false)}
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zm0-10v2h14V7H7z" />
                                                        </svg>
                                                        Administration
                                                    </Link>
                                                )}



                                                <div className="menu-separator"></div>

                                                <button className="user-menu-item logout-item" onClick={handleLogout}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                                                    </svg>
                                                    Déconnexion
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </li>
                        )}
                    </ul>
                </div>

                {/* Toggle menu mobile - Visible uniquement sur mobile */}
                <div className={`menu-toggle ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(!menuOpen)}>
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </div>

            {/* Mobile Sidebar */}
            <div className={`mobile-sidebar ${menuOpen ? 'open' : ''}`}>
                <div className="close-sidebar" onClick={() => setMenuOpen(false)}>
                    <span></span>
                    <span></span>
                </div>
                <ul className="sidebar-links">
                    <li>
                        <Link to="/" onClick={closeMenu} className="sidebar-main-link">
                            Accueil
                        </Link>
                    </li>
                    <li>
                        <Link to="/mecanique" onClick={closeMenu} className="sidebar-main-link">
                            Mécanique
                        </Link>
                    </li>
                    <li>
                        <Link to="/vente-auto" onClick={closeMenu} className="sidebar-main-link">
                            Vente Auto
                        </Link>
                    </li>
                    <li>
                        <Link to="/blog" onClick={closeMenu} className="sidebar-main-link">
                            Blog
                        </Link>
                    </li>
                    <li>
                        <Link to="/contact" onClick={closeMenu} className="sidebar-main-link">
                            Contact
                        </Link>
                    </li>
                    <div className="mobile-separator"></div>
                    {!isAuthenticated ? (
                        <>
                            <li className="auth-mobile">
                                <button onClick={() => openAuthModal('login')} className="auth-btn-mobile">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                    Connexion
                                </button>
                            </li>
                            <li className="auth-mobile">
                                <button
                                    onClick={() => {
                                        setMenuOpen(false);
                                        navigate('/inscription');
                                    }}
                                    className="auth-btn-mobile register"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                    Inscription
                                </button>
                            </li>
                        </>
                    ) : (
                        <>
                            <li className="auth-mobile">
                                <div className="user-avatar-mobile">
                                    <div className="avatar-circle-mobile">
                                        {getUserInitial()}
                                    </div>
                                    <span className="user-name-mobile">{user?.name || user?.email}</span>
                                </div>
                            </li>
                            <li className="auth-mobile">
                                <button
                                    type="button"
                                    className="auth-btn-mobile"
                                    onClick={() => {
                                        closeMenu();
                                        navigate('/mon-compte');
                                    }}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                    Mon Compte
                                </button>
                            </li>
                            {isAdmin && (
                                <li className="auth-mobile">
                                    <button
                                        type="button"
                                        className="auth-btn-mobile"
                                        onClick={() => {
                                            closeMenu();
                                            navigate('/admin');
                                        }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zm0-10v2h14V7H7z" />
                                        </svg>
                                        Administration
                                    </button>
                                </li>
                            )}

                            <li className="auth-mobile">
                                <button onClick={handleLogout} className="auth-btn-mobile logout">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                                    </svg>
                                    Déconnexion
                                </button>
                            </li>
                        </>
                    )}
                </ul>
            </div>

            {/* Overlay for mobile sidebar */}
            {menuOpen && <div className="sidebar-overlay open" onClick={() => setMenuOpen(false)}></div>}

            <style jsx>{`
                /* Styles globaux selon le contexte de route */
                body.public-style {
                    background-color: #ffffff;
                }

                body.admin-style {
                    background-color: #f4f7f9;
                }

                body.admin-style .navbar {
                    background-color: #1A365D;
                    color: white;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
                }

                body.admin-style .desktop-links li a {
                    color: white;
                }

                body.admin-style .desktop-links li a:hover {
                    color: #007bff;
                }

                body.admin-style .avatar-circle {
                    background: white;
                    color: #1A365D;
                }

                .navbar {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    background-color: transparent; /* Transparent par défaut */
                    color: white;
                    padding: 15px 0;
                    z-index: 1000;
                    transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
                }

                .navbar.scrolled {
                    background-color: #ffffff; /* Blanc au défilement */
                    color: #1A365D; /* Texte bleu foncé au défilement */
                    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
                }

                .navbar-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 0 30px;
                    width: 100%;
                }

                /* Système de 3 colonnes pour le centrage parfait */
                .nav-section {
                    display: flex;
                    align-items: center;
                }

                .left-section {
                    flex: 1;
                    justify-content: flex-start;
                }

                .center-section {
                    flex: 2;
                    justify-content: center;
                }

                .right-section {
                    flex: 1;
                    justify-content: flex-end;
                }

                .navbar-brand {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    font-size: 1.8rem;
                    font-weight: bold;
                    text-decoration: none;
                    color: inherit; /* Hérite la couleur du parent (navbar) */
                    white-space: nowrap;
                }

                .navbar-logo-img {
                    width: 42px;
                    height: 42px;
                    border-radius: 50%;
                    object-fit: cover;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    transition: all 0.3s ease;
                }

                .navbar.scrolled .navbar-logo-img {
                    border-color: rgba(26, 54, 93, 0.2);
                }

                .desktop-links {
                    list-style: none;
                    margin: 0;
                    padding: 0;
                    display: flex;
                    gap: 25px;
                    align-items: center; /* Align icons vertically */
                }

                .navbar-actions {
                    list-style: none;
                    margin: 0;
                    padding: 0;
                    display: flex;
                    align-items: center;
                }

                .desktop-links li a {
                    text-decoration: none;
                    color: inherit; /* Hérite la couleur du parent (navbar) */
                    font-size: 1.1rem;
                    font-weight: 500;
                    padding: 5px 0;
                    position: relative;
                    transition: color 0.3s ease;
                }

                .desktop-links li a:hover {
                    color: #007bff; /* Un joli bleu pour l'effet de survol */
                }

                .desktop-links li a.active {
                    color: #007bff;
                }

                .desktop-links li a::after {
                    content: '';
                    position: absolute;
                    width: 0;
                    height: 2px;
                    bottom: -3px;
                    left: 0;
                    background-color: #007bff; /* Soulignement bleu */
                    transition: width 0.3s ease-in-out;
                }

                .desktop-links li a:hover::after,
                .desktop-links li a.active::after {
                    width: 100%;
                }

                /* Auth buttons styling for desktop */
                .auth-desktop {
                    margin-left: 0;
                }

                .auth-desktop + .auth-desktop {
                    margin-left: -5px; /* Rapproche encore plus les deux boutons */
                }

                .auth-btn-desktop {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 20px;
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    border-radius: 4px; /* Rectangulaire */
                    background: transparent;
                    color: inherit;
                    font-size: 0.9rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .navbar.scrolled .auth-btn-desktop {
                    border-color: rgba(26, 54, 93, 0.2);
                }

                .auth-btn-desktop:hover {
                    background: rgba(255, 255, 255, 0.1);
                    border-color: white;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }

                .navbar.scrolled .auth-btn-desktop:hover {
                    background: rgba(26, 54, 93, 0.05);
                    border-color: #1A365D;
                }

                .auth-btn-desktop.register {
                    background: #ffffff;
                    color: #1A365D;
                    border: none;
                }

                .navbar.scrolled .auth-btn-desktop.register {
                    background: #1A365D;
                    color: white;
                }

                .auth-btn-desktop.register:hover {
                    background: #f8fafc;
                    transform: translateY(-2px);
                    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2);
                }

                .navbar.scrolled .auth-btn-desktop.register:hover {
                    background: #122849;
                }

                .auth-btn-desktop.logout {
                    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
                    color: white;
                }

                .auth-btn-desktop.logout:hover {
                    background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
                }

                /* User menu container */
                .user-menu-container {
                    position: relative;
                }

                .user-avatar-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 4px 8px;
                    border: none;
                    background: transparent;
                    cursor: pointer;
                    border-radius: 20px;
                    transition: all 0.3s ease;
                    color: inherit;
                }

                .user-avatar-btn:hover {
                    background: rgba(0, 123, 255, 0.1);
                }

                .avatar-circle {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: 600;
                    font-size: 0.9rem;
                    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
                }

                .dropdown-arrow {
                    transition: transform 0.3s ease;
                    opacity: 0.7;
                }

                .dropdown-arrow.rotated {
                    transform: rotate(180deg);
                }

                /* Dropdown menu */
                .user-dropdown-menu {
                    position: absolute;
                    top: 100%;
                    right: 0;
                    margin-top: 8px;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    min-width: 280px;
                    z-index: 1100;
                    overflow: hidden;
                    animation: dropdownAppear 0.2s ease-out;
                }

                @keyframes dropdownAppear {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .user-info-header {
                    padding: 20px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .avatar-circle-large {
                    width: 48px;
                    height: 48px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: 600;
                    font-size: 1.2rem;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
                }

                .user-details {
                    flex: 1;
                }

                .user-name {
                    font-weight: 600;
                    color: #1f2937;
                    font-size: 1rem;
                    margin-bottom: 2px;
                }

                .user-email {
                    color: #6b7280;
                    font-size: 0.85rem;
                }

                .menu-separator {
                    height: 1px;
                    background: #e5e7eb;
                    margin: 0 12px;
                }

                /* Conteneur des items du menu utilisateur */
                .user-dropdown-menu .menu-items {
                    padding: 8px;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                /* Items du menu utilisateur (Mon Compte / Administration / Paramètres / Déconnexion) */
                .user-dropdown-menu .user-menu-item {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 10px 12px;
                    border: none;
                    background: none;
                    text-align: left;
                    cursor: pointer;
                    transition: background-color 0.2s ease;
                    color: #374151 !important;
                    font-size: 0.9rem;
                    text-decoration: none !important;
                    border-radius: 8px;
                }

                .user-dropdown-menu .user-menu-item::after {
                    content: none !important;
                }

                .user-dropdown-menu .user-menu-item:hover {
                    background: #f3f4f6;
                }

                .user-dropdown-menu .user-menu-item.logout-item {
                    color: #dc2626;
                }

                .user-dropdown-menu .user-menu-item.logout-item:hover {
                    background: #fef2f2;
                }

.menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
  color: #374151;
  font-size: 0.9rem;
}

.menu-item:hover {
  background: #f3f4f6;
}

/* Variante simple : pas d'effet de survol pour Mon Compte / Administration */
.menu-item-simple {
  cursor: pointer;
}

.menu-item-simple:hover {
  background: none;
}

                .menu-item:hover {
                    background: #f3f4f6;
                }

                .menu-item.logout-item {
                    background: none;
                }

                /* Liens simples Mon Compte / Administration (desktop) */
                .menu-item-plain {
                    width: 100%;
                    display: block;
                    padding: 10px 20px;
                    background: none;
                    border: none;
                    text-align: left;
                    text-decoration: none;
                    color: #374151;
                    font-size: 0.9rem;
                    cursor: pointer;
                }

                .menu-item-plain:hover {
                    background: none;
                    color: #374151;
                }

                /* Styles spécifiques pour le bouton Contact (si utilisé comme un bouton distinct) */
                .navbar-contact-btn {
                    background-color: #1A365D;
                    color: white;
                    padding: 10px 20px;
                    border-radius: 8px;
                    transition: background-color 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease;
                    font-weight: 500;
                    text-decoration: none;
                    position: relative;
                    z-index: 1;
                    outline: none;
                    box-shadow: none;
                }

                .navbar-contact-btn:hover {
                    background-color: #122849;
                    transform: translateY(-2px);
                    color: white;
                    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
                }

                .navbar-contact-btn::after {
                    content: none; 
                }

                .navbar-contact-btn:focus {
                    outline: none;
                    box-shadow: none;
                }

                /* Styles du menu hamburger */
                .menu-toggle {
                    display: none; /* Caché par défaut sur les grands écrans */
                    flex-direction: column;
                    cursor: pointer;
                    gap: 5px;
                    width: 30px;
                    height: 20px;
                    justify-content: space-between;
                }

                .menu-toggle span {
                    display: block;
                    width: 100%;
                    height: 3px;
                    background-color: currentColor; /* Sera blanc ou bleu foncé */
                    border-radius: 2px;
                    transition: all 0.3s ease-in-out;
                }

                .menu-toggle.open span:nth-child(1) {
                    transform: translateY(8px) rotate(45deg);
                }

                .menu-toggle.open span:nth-child(2) {
                    opacity: 0;
                }

                .menu-toggle.open span:nth-child(3) {
                    transform: translateY(-8px) rotate(-45deg);
                }

                /* Mobile Sidebar Styles */
                .mobile-sidebar {
                    position: fixed;
                    top: 0;
                    right: -100%; /* Start off-screen to the right */
                    width: 70%; /* Adjust as needed */
                    max-width: 300px; /* Max width for larger mobile devices */
                    height: 100vh;
                    background-color: #ffffff;
                    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
                    transition: right 0.3s ease-in-out;
                    z-index: 999; /* Below the navbar, but above content */
                    padding-top: 20px; /* Adjusted to give space at the top */
                    display: flex;
                    flex-direction: column;
                    overflow-y: auto; /* Allow vertical scroll */
                    -webkit-overflow-scrolling: touch; /* Smooth scroll on iOS */
                }

                .mobile-sidebar.open {
                    right: 0; /* Slide in */
                }

                .close-sidebar {
                    position: absolute;
                    top: 20px;
                    right: 20px;
                    width: 30px;
                    height: 30px;
                    cursor: pointer;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    z-index: 1001; /* Ensure it's above other sidebar content */
                }

                .close-sidebar span {
                    display: block;
                    width: 100%;
                    height: 3px;
                    background-color: #1A365D; /* Dark blue for the cross */
                    border-radius: 2px;
                    position: absolute;
                    transition: all 0.3s ease-in-out;
                }

                .close-sidebar span:nth-child(1) {
                    transform: rotate(45deg);
                }

                .close-sidebar span:nth-child(2) {
                    transform: rotate(-45deg);
                }

                .sidebar-links {
                    list-style: none;
                    padding: 0;
                    margin-top: 60px; /* Space for the close button */
                    flex-grow: 1; /* Allow links to take up available space */
                    padding-bottom: 24px; /* Space at bottom for scroll */
                }

                .sidebar-links li {
                    text-align: center;
                    margin: 0;
                }

                /* Liens du menu principal dans la sidebar (Accueil, Mécanique, etc.) */
                .sidebar-main-link {
                    display: block;
                    padding: 15px 20px;
                    text-decoration: none;
                    color: #1A365D;
                    font-size: 1.2rem;
                    font-weight: 500;
                    transition: background-color 0.2s ease;
                }

                .sidebar-main-link:hover {
                    background-color: #f0f4f8;
                }

                .sidebar-links li a.active {
                    background-color: #e3f2fd;
                    color: #007bff;
                }

                /* Mobile separator style */
                .mobile-separator {
                    width: 80%;
                    height: 1px;
                    background-color: #e0e0e0;
                    margin: 20px auto; /* Centered with vertical spacing */
                }

                /* Zone auth dans le menu mobile (avatar + liens compte/admin/paramètres) */
                .auth-mobile {
                    margin-top: 0;
                    padding: 0 20px;
                    text-align: left;
                }

                .auth-btn-mobile {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    padding: 12px 0;
                    width: 100%;
                    border: 1px solid rgba(26, 54, 93, 0.2);
                    background: transparent;
                    color: #1A365D;
                    font-size: 1.1rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    border-radius: 4px; /* Rectangulaire */
                    text-decoration: none;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .auth-btn-mobile:hover {
                    background: rgba(0, 123, 255, 0.05);
                    border-color: #1A365D;
                }

                .auth-btn-mobile.register {
                    background: #1A365D;
                    color: white;
                    border: none;
                    margin-top: 10px;
                }

                .auth-btn-mobile.register:hover {
                    background: #122849;
                }

                .auth-btn-mobile.logout {
                    background: #dc3545;
                    color: white;
                    border: none;
                    margin-top: 10px;
                }

                .auth-btn-mobile.logout:hover {
                    background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
                }

                .user-avatar-mobile {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    padding: 12px 0;
                    background: rgba(0, 123, 255, 0.1);
                    border-radius: 8px;
                    color: #007bff;
                    font-size: 1.1rem;
                    font-weight: 500;
                }

                .avatar-circle-mobile {
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: 600;
                    font-size: 1rem;
                    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
                }

                .user-name-mobile {
                    flex: 1;
                    text-align: left;
                }

                .sidebar-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent black */
                    z-index: 998; /* Below sidebar, above main content */
                    transition: opacity 0.3s ease-in-out;
                    opacity: 0;
                    pointer-events: none; /* Allow clicks through when hidden */
                }

                .sidebar-overlay.open {
                    opacity: 1;
                    pointer-events: auto; /* Enable clicks when visible */
                }

                /* Media Queries pour la réactivité */
                @media (max-width: 992px) {
                    .desktop-only {
                        display: none !important;
                    }
                    
                    .center-section, .right-section {
                        display: none;
                    }
                    
                    .left-section {
                        flex: 1;
                        justify-content: flex-start;
                    }
                }

                @media (max-width: 768px) {
                    .menu-toggle {
                        display: flex; /* Affiche le menu hamburger */
                    }

                    .navbar-brand {
                        font-size: 1.5rem;
                    }

                    .sidebar-overlay {
                        opacity: 0; /* Ensures it starts hidden on mobile */
                        pointer-events: none;
                    }
                    .sidebar-overlay.open {
                        opacity: 1;
                        pointer-events: auto;
                    }
                }

                @media (max-width: 480px) {
                    .navbar-brand {
                        font-size: 1.3rem;
                    }
                }
            `}</style>

            <style jsx global>{`
                html, body, #root, #__next {
                    overflow-x: hidden !important;
                    width: 100%;
                }
                *, *::before, *::after { box-sizing: border-box; }
            `}</style>

            {/* Modal d'authentification */}
            <AuthModal
                isOpen={showAuthModal}
                onClose={closeAuthModal}
                mode={authMode}
                onModeChange={setAuthMode}
            />
        </nav>
    );
};

export default Navbar;